<template>
  <div class="matching-question-line">
    <svg class="matching-svg" :width="svgWidth" :height="svgHeight">
      <g v-for="(line, idx) in lines" :key="idx">
        <line
          :x1="line.x1"
          :y1="line.y1"
          :x2="line.x2"
          :y2="line.y2"
          :stroke="line.color"
          stroke-width="7"
          stroke-linecap="round"
          class="interactive-line"
          :class="{ 'active-line': line.active }"
          @click.stop="removeLine(line.leftIdx, line.rightIdx)"
          @mouseover="onLineHover(idx)"
          @mouseout="onLineMouseOut()"
          :style="{ opacity: hoverLine === idx ? 0.7 : 1, cursor: 'pointer', pointerEvents: drawing ? 'none' : 'auto', stroke: hoverLine === idx ? '#f44336' : line.color }"
        />
        <g v-if="hoverLine === idx">
          <circle :cx="(line.x1 + line.x2) / 2" :cy="(line.y1 + line.y2) / 2" r="13" fill="#fff" stroke="#f44336" stroke-width="2" style="cursor:pointer" @click.stop="removeLine(line.leftIdx, line.rightIdx)"/>
          <text :x="(line.x1 + line.x2) / 2" :y="(line.y1 + line.y2) / 2 + 5" text-anchor="middle" font-size="18" fill="#f44336" font-weight="bold" style="pointer-events:none">×</text>
        </g>
      </g>
      <line
        v-if="drawingLine"
        :x1="drawingLine.x1"
        :y1="drawingLine.y1"
        :x2="drawingLine.x2"
        :y2="drawingLine.y2"
        stroke="#90caf9"
        stroke-width="2"
        stroke-dasharray="4"
        style="pointer-events: none;"
      />
    </svg>
    <div class="columns">
      <div class="column left">
        <ul style="padding:0;">
          <li v-for="(item, lIdx) in left" :key="lIdx" class="line-item line-item-hoverable"
              @mousedown.stop="startLine(lIdx)"
              @touchstart.stop.prevent="startLine(lIdx)">
            <div class="line-row">
              <div class="item-label">{{ String.fromCharCode(65 + lIdx) }}.</div>
              <div class="item-content" v-sanitize="processImageHTML(item)"></div>
              <div
                class="dot dot-left"
                :ref="'left-dot-' + lIdx"
                :tabindex="0"
                :aria-label="`Pilih pertanyaan ${lIdx + 1}`"
                @mousedown.stop="startLine(lIdx)"
                @touchstart.stop.prevent="startLine(lIdx)"
                @keydown.enter.space.stop="startLine(lIdx)"
                :class="{ 'dot-active': drawing && drawingFrom === lIdx }"
              ></div>
            </div>
          </li>
        </ul>
      </div>
      <div class="column right">
        <ul style="padding: 0;">
          <li v-for="(item, rIdx) in right" :key="rIdx" class="line-item line-item-hoverable"
              @mouseup.stop="endLine(rIdx)"
              @touchend.stop.prevent="endLine(rIdx)">
            <div class="line-row">
              <div
                class="dot dot-right"
                :ref="'right-dot-' + rIdx"
                :tabindex="0"
                :aria-label="`Pilih jawaban ${rIdx + 1}`"
                @mouseup.stop="endLine(rIdx)"
                @touchend.stop.prevent="endLine(rIdx)"
                @touchstart.stop.prevent="checkTouchEndLine(rIdx, $event)"
                @keydown.enter.space.stop="endLine(rIdx)"
                :class="{ 'dot-active': drawing && drawingTo === rIdx }"
              ></div>
              <div class="item-label">{{ rIdx + 1 }}.</div>
              <div class="item-content" v-sanitize="processImageHTML(item)"></div>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <button class="reset-btn" @click="showResetDialog = true" style="margin-top:10px">Reset</button>
    
    <!-- Dialog Reset yang lebih bagus -->
    <div v-if="showResetDialog" class="dialog-overlay" @click="showResetDialog = false">
      <div class="reset-dialog" @click.stop>
        <div class="dialog-header">
          <div class="dialog-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#ff9800"/>
            </svg>
          </div>
          <h3 class="dialog-title">Reset Semua Pasangan?</h3>
        </div>
        
        <div class="dialog-content">
          <p class="dialog-message">
            Semua garis yang telah kamu buat akan dihapus dan tidak dapat dikembalikan.
          </p>
          <div class="dialog-stats" v-if="totalConnections > 0">
            <span class="stats-text">{{ totalConnections }} pasangan akan dihapus</span>
          </div>
        </div>
        
        <div class="dialog-actions">
          <button class="dialog-btn dialog-btn-cancel" @click="showResetDialog = false">
            Batal
          </button>
          <button class="dialog-btn dialog-btn-confirm" @click="executeReset">
            Ya, Reset
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MatchingLine',
  props: {
    left: { type: Array, required: true },
    right: { type: Array, required: true },
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      drawing: false,
      drawingFrom: null,
      drawingTo: null,
      drawingLine: null,
      svgWidth: 700,
      svgHeight: 400,
      hoverLine: null, // index garis yang sedang di-hover
      resizeObserver: null,
      autoScrollInterval: null, // untuk auto scroll
      autoScrollDirection: null, // 'up' | 'down' | null
      showResetDialog: false, // untuk dialog reset yang lebih bagus
    };
  },
  mounted() {
    // Reset hover state on mount
    this.hoverLine = null;
    
    this.updateSvgSize();
    window.addEventListener('resize', this.updateSvgSize);
    // Gunakan ResizeObserver agar svgWidth selalu mengikuti parent
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateSvgSize();
      });
      this.resizeObserver.observe(this.$el);
    }
  },
  updated() {
    this.$nextTick(this.updateSvgSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateSvgSize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // Cleanup: restore body scroll if component is destroyed with dialog open
    document.body.style.overflow = '';
  },
  computed: {
    lines() {
      const colors = [
        '#2196f3', // biru
        '#43a047', // hijau
        '#ff9800', // oranye
        '#9c27b0', // ungu
        '#00bcd4', // cyan
        '#fbc02d', // kuning gelap
        '#607d8b', // abu-abu tua
        '#ad1457', // pink tua
        '#00897b', // teal
        '#8d6e63'  // coklat
      ];
      const lines = [];
      this.left.forEach((l, lIdx) => {
        (this.value[lIdx] || []).forEach((rIdx) => {
          const leftDotRef = this.$refs['left-dot-' + lIdx];
          const rightDotRef = this.$refs['right-dot-' + rIdx];
          const leftDot = Array.isArray(leftDotRef) ? leftDotRef[0] : leftDotRef;
          const rightDot = Array.isArray(rightDotRef) ? rightDotRef[0] : rightDotRef;
          if (leftDot && rightDot) {
            const leftRect = leftDot.getBoundingClientRect();
            const rightRect = rightDot.getBoundingClientRect();
            const parentRect = this.$el.getBoundingClientRect();
            lines.push({
              x1: leftRect.right - parentRect.left,
              y1: leftRect.top + leftRect.height / 2 - parentRect.top,
              x2: rightRect.left - parentRect.left,
              y2: rightRect.top + rightRect.height / 2 - parentRect.top,
              color: colors[(lIdx + rIdx) % colors.length],
              active: false, // hanya aktif saat di-hover
              leftIdx: lIdx,
              rightIdx: rIdx
            });
          }
        });
      });
      return lines;
    },
    totalConnections() {
      // Hitung total koneksi yang sudah dibuat
      let count = 0;
      this.value.forEach(connections => {
        if (Array.isArray(connections)) {
          count += connections.length;
        }
      });
      return count;
    }
  },
  methods: {
    updateSvgSize() {
      this.$nextTick(() => {
        const columns = this.$el.querySelector('.columns');
        let height = columns ? columns.offsetHeight : 0;
        let width = this.$el ? this.$el.offsetWidth : 0;
        // Fallback jika height/width 0
        if (!height || height < 50) height = 400;
      if (!width || width < 50) width = 320;
        this.svgHeight = height;
        this.svgWidth = width;
      });
    },
    processImageHTML(htmlString) {
      // Add draggable="false" to all img tags to prevent image dragging
      if (typeof htmlString === 'string') {
        return htmlString.replace(/<img([^>]*)>/gi, '<img$1 draggable="false">');
      }
      return htmlString;
    },
    isTouchDevice() {
      // Detect if device supports touch - more reliable method
      return ('ontouchstart' in window) || 
             (navigator.maxTouchPoints > 0) || 
             (navigator.msMaxTouchPoints > 0);
    },
    onLineHover(idx) {
      // Only allow hover effects on desktop (non-touch devices)
      if (!this.isTouchDevice()) {
        this.hoverLine = idx;
      }
    },
    onLineMouseOut() {
      // Only clear hover on desktop
      if (!this.isTouchDevice()) {
        this.hoverLine = null;
      }
    },
    // (removed stray <svg ...> line)
    startLine(lIdx) {
      this.drawing = true;
      this.drawingFrom = lIdx;
      this.drawingTo = null;
      const leftDotRef = this.$refs['left-dot-' + lIdx];
      const leftDot = Array.isArray(leftDotRef) ? leftDotRef[0] : leftDotRef;
      const parentRect = this.$el.getBoundingClientRect();
      const rect = leftDot.getBoundingClientRect();
      this.drawingLine = {
        x1: rect.right - parentRect.left,
        y1: rect.top + rect.height / 2 - parentRect.top,
        x2: rect.right - parentRect.left,
        y2: rect.top + rect.height / 2 - parentRect.top,
      };
      window.addEventListener('mousemove', this.moveLine);
      window.addEventListener('mouseup', this.cancelLine);
      window.addEventListener('touchmove', this.moveLineTouch);
      window.addEventListener('touchend', this.handleTouchEnd);
    },
    moveLine(e) {
      if (!this.drawingLine) return;
      const parentRect = this.$el.getBoundingClientRect();
      this.drawingLine.x2 = e.clientX - parentRect.left;
      this.drawingLine.y2 = e.clientY - parentRect.top;

    // Auto scroll saat drag (mouse)
    this.handleAutoScroll(e.clientY);
    },
    moveLineTouch(e) {
      if (!this.drawingLine) return;
      e.preventDefault();
      const touch = e.touches[0];
      const parentRect = this.$el.getBoundingClientRect();
      this.drawingLine.x2 = touch.clientX - parentRect.left;
      this.drawingLine.y2 = touch.clientY - parentRect.top;
      
      // Check if touch is over any right item (box or dot)
      const touchX = touch.clientX;
      const touchY = touch.clientY;
      this.right.forEach((item, rIdx) => {
        const rightItemRef = this.$el.querySelectorAll('.column.right .line-item')[rIdx];
        if (rightItemRef) {
          const itemRect = rightItemRef.getBoundingClientRect();
          const isOverItem = touchX >= itemRect.left && touchX <= itemRect.right && 
                           touchY >= itemRect.top && touchY <= itemRect.bottom;
          if (isOverItem) {
            this.drawingTo = rIdx;
          }
        }
      });

    // Auto scroll saat drag (touch)
    this.handleAutoScroll(touch.clientY);
    },
    handleAutoScroll(pointerY) {
      // pointerY: posisi Y absolute (clientY)
      // Scroll parent container jika pointer mendekati tepi atas/bawah
      const SCROLL_ZONE = 60; // px dari atas/bawah area
      const SCROLL_SPEED = 18; // px per interval
      const INTERVAL = 18; // ms
      const container = this.getScrollableContainer();
      if (!container) return;
      const rect = container.getBoundingClientRect();
      let direction = null;
      if (pointerY - rect.top < SCROLL_ZONE) {
        direction = 'up';
      } else if (rect.bottom - pointerY < SCROLL_ZONE) {
        direction = 'down';
      }
      if (direction) {
        if (!this.autoScrollInterval || this.autoScrollDirection !== direction) {
          this.clearAutoScroll();
          this.autoScrollDirection = direction;
          this.autoScrollInterval = setInterval(() => {
            if (direction === 'up') {
              container.scrollTop = Math.max(0, container.scrollTop - SCROLL_SPEED);
            } else if (direction === 'down') {
              container.scrollTop = Math.min(container.scrollHeight - container.clientHeight, container.scrollTop + SCROLL_SPEED);
            }
          }, INTERVAL);
        }
      } else {
        this.clearAutoScroll();
      }
    },

    clearAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
        this.autoScrollDirection = null;
      }
    },

    getScrollableContainer() {
      // Cari parent yang bisa discroll (overflow-y: auto/scroll)
      let el = this.$el;
      while (el) {
        const style = window.getComputedStyle(el);
        if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    },
    checkTouchEndLine(rIdx, event) {
      // This method helps with touch event detection on right dots
      if (this.drawing) {
        event.preventDefault();
        event.stopPropagation();
        this.endLine(rIdx);
      }
    },
    endLine(rIdx) {
      if (!this.drawing) return;
      let userMatches = this.value ? JSON.parse(JSON.stringify(this.value)) : this.left.map(() => []);
      if (!userMatches[this.drawingFrom]) userMatches[this.drawingFrom] = [];
      if (!userMatches[this.drawingFrom].includes(rIdx)) userMatches[this.drawingFrom].push(rIdx);
      this.$emit('input', userMatches);
      this.drawing = false;
      this.drawingFrom = null;
      this.drawingTo = null;
      this.drawingLine = null;
      this.clearAutoScroll();
      window.removeEventListener('mousemove', this.moveLine);
      window.removeEventListener('mouseup', this.cancelLine);
      window.removeEventListener('touchmove', this.moveLineTouch);
      window.removeEventListener('touchend', this.handleTouchEnd);
    },
    cancelLine() {
      // Check if we're ending on a right dot before canceling
      if (this.drawing && this.drawingTo !== null) {
        this.endLine(this.drawingTo);
        return;
      }
      
      this.drawing = false;
      this.drawingFrom = null;
      this.drawingTo = null;
      this.drawingLine = null;
      this.clearAutoScroll();
      window.removeEventListener('mousemove', this.moveLine);
      window.removeEventListener('mouseup', this.cancelLine);
      window.removeEventListener('touchmove', this.moveLineTouch);
      window.removeEventListener('touchend', this.handleTouchEnd);
    },
    handleTouchEnd(e) {
      if (!this.drawing) return;
      
      e.preventDefault();
      const touch = e.changedTouches[0];
      const touchX = touch.clientX;
      const touchY = touch.clientY;
      
      // Check if touch ended on any right item (box or dot)
      let foundDot = null;
      this.right.forEach((item, rIdx) => {
        const rightItemRef = this.$el.querySelectorAll('.column.right .line-item')[rIdx];
        if (rightItemRef) {
          const itemRect = rightItemRef.getBoundingClientRect();
          const isOverItem = touchX >= itemRect.left - 10 && touchX <= itemRect.right + 10 && 
                           touchY >= itemRect.top - 10 && touchY <= itemRect.bottom + 10;
          if (isOverItem) {
            foundDot = rIdx;
          }
        }
      });
      
      if (foundDot !== null) {
        this.endLine(foundDot);
      } else {
        this.cancelLine();
      }
    },
    removeMatch(lIdx, rIdx) {
      let userMatches = this.value ? JSON.parse(JSON.stringify(this.value)) : this.left.map(() => []);
      if (!userMatches[lIdx]) userMatches[lIdx] = [];
      const ix = userMatches[lIdx].indexOf(rIdx);
      if (ix !== -1) userMatches[lIdx].splice(ix, 1);
      this.$emit('input', userMatches);
    },
    removeLine(leftIdx, rightIdx) {
      // Method baru untuk menghapus garis langsung dari klik SVG
      this.removeMatch(leftIdx, rightIdx);
    },
    resetMatches() {
      this.$emit('input', this.left.map(() => []));
    },
    executeReset() {
      this.resetMatches();
      this.showResetDialog = false;
    },
    confirmReset() {
      this.showResetDialog = true;
    }
  },
  watch: {
    drawing(val) {
      if (!val) {
        this.drawingLine = null;
        // Reset hover state after drawing, especially on touch devices
        if (this.isTouchDevice()) {
          this.hoverLine = null;
        }
      }
    },
    showResetDialog(val) {
      // Prevent body scroll when dialog is open
      if (val) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  }
};
</script>

<style scoped>
.matching-question-line {
  max-width: 1300px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  box-sizing: border-box;
  padding: 0px 10px 32px 10px;
}
.matching-svg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  max-width: 100vw;
}
.interactive-line {
  transition: opacity 0.2s ease;
}
.interactive-line:hover {
  filter: drop-shadow(0 0 6px #f44336);
}
.active-line {
  filter: drop-shadow(0 0 4px #2196f3);
}
.columns {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 1;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  margin-bottom: 8px;
}
.column {
  width: calc(50% - 10px);
  min-width: 180px;
  max-width: 100%;
  box-sizing: border-box;
}
.line-item {
  background: #f8f9fa;
  margin: 10px 0;
  padding: 15px;
  border-radius: 8px;
  position: relative;
  transition: background 0.2s;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  user-select: none;
  border: 1px solid #e0e0e0;
  min-height: 70px;
  box-sizing: border-box;
}
.line-item:hover {
  background: #f0f0f0;
  cursor: pointer;
}
.line-item-hoverable {
  cursor: pointer;
}
.line-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 40px;
}
.left .line-row {
  flex-direction: row;
}
.right .line-row {
  flex-direction: row;
}
.item-content {
  flex: 1;
  word-break: break-word;
  line-height: 1.4;
}
.item-label {
  font-weight: bold;
  color: #1976d2;
  margin-right: 8px;
  font-size: 16px;
  min-width: 24px;
  flex-shrink: 0;
}
.item-content >>> img {
  /* max-width: 100%;
  max-height: 60px; */
  object-fit: contain;
  display: block;
  margin: 5px auto;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  pointer-events: none;
  user-select: none;
}
.item-content >>> p {
  object-fit: contain;
  display: block;
  margin: 5px auto;
}
.dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #4CC5BD;
  display: inline-block;
  cursor: pointer;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(33,150,243,0.2);
  transition: all 0.2s;
  flex-shrink: 0;
}
.dot:hover, .dot:focus {
  background: #2fa89e;
  box-shadow: 0 0 0 4px #90caf9;
  outline: none;
  transform: scale(1.1);
}
.dot-active {
  background: #ff9800 !important;
  box-shadow: 0 0 0 4px #ffb74d !important;
  transform: scale(1.2) !important;
}
.dot-left {
  margin-left: 10px;
  margin-right: 0;
}
.dot-right {
  margin-right: 10px;
  margin-left: 0;
}
.reset-btn {
  background: #fff;
  color: #1976d2;
  border: 2px solid #90caf9;
  border-radius: 8px;
  padding: 8px 20px;
  font-weight: 600;
  transition: all 0.2s;
  cursor: pointer;
  margin-top: 15px;
}
  .reset-btn:hover {
    background: #e3f2fd;
    color: #f44336;
    border-color: #f44336;
    transform: translateY(-1px);
  }

  /* Dialog Reset Styles */
  .dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease-out;
  }

  .reset-dialog {
    background: white;
    border-radius: 16px;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.15);
    max-width: 400px;
    width: 90%;
    max-height: 90vh;
    overflow: hidden;
    animation: slideIn 0.3s ease-out;
  }

  .dialog-header {
    padding: 24px 24px 16px 24px;
    text-align: center;
    border-bottom: 1px solid #f0f0f0;
  }

  .dialog-icon {
    margin-bottom: 12px;
  }

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
    line-height: 1.3;
  }

  .dialog-content {
    padding: 20px 24px;
    text-align: center;
  }

  .dialog-message {
    margin: 0 0 16px 0;
    font-size: 15px;
    color: #666;
    line-height: 1.5;
  }

  .dialog-stats {
    background: #fff3e0;
    border: 1px solid #ffcc02;
    border-radius: 8px;
    padding: 8px 12px;
    margin-top: 12px;
  }

  .stats-text {
    font-size: 13px;
    color: #f57700;
    font-weight: 500;
  }

  .dialog-actions {
    padding: 16px 24px 24px 24px;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  .dialog-btn {
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    min-width: 80px;
  }

  .dialog-btn-cancel {
    background: #f5f5f5;
    color: #666;
  }

  .dialog-btn-cancel:hover {
    background: #e0e0e0;
    color: #333;
  }

  .dialog-btn-confirm {
    background: #f44336;
    color: white;
  }

  .dialog-btn-confirm:hover {
    background: #d32f2f;
    transform: translateY(-1px);
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideIn {
    from { 
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  /* Mobile responsiveness untuk dialog */
  @media (max-width: 480px) {
    .reset-dialog {
      width: 95%;
      margin: 20px;
    }
    
    .dialog-header {
      padding: 20px 20px 12px 20px;
    }
    
    .dialog-title {
      font-size: 18px;
    }
    
    .dialog-content {
      padding: 16px 20px;
    }
    
    .dialog-message {
      font-size: 14px;
    }
    
    .dialog-actions {
      padding: 12px 20px 20px 20px;
      flex-direction: column;
    }
    
    .dialog-btn {
      width: 100%;
      margin-bottom: 8px;
    }
    
    .dialog-btn:last-child {
      margin-bottom: 0;
    }
  }/* Disable hover effects on touch devices */
@media (hover: none) and (pointer: coarse) {
  .interactive-line:hover {
    filter: none !important;
  }
}

/* Tablet breakpoint (1024px - 769px) */
@media (max-width: 1024px) and (min-width: 769px) {
  .matching-question-line {
    max-width: 100vw;
    padding: 0px 2vw 28px 2vw;
  }
  .columns {
    gap: 15px;
  }
  .column {
    min-width: 160px;
  }
  .line-item {
    padding: 12px;
    min-height: 60px;
    /* Removed pointer-events: none to allow drag from entire item area on tablet */
  }
  /* Disable hover effects on tablet only */
  .interactive-line:hover {
    filter: none !important;
  }
  .item-content >>> img {
    max-width: 100%;
  }
  .item-content >>> p {
    max-width: 100%;
  }
  .matching-instructions {
    font-size: 13px;
    padding: 7px 12px;
  }
  .dot {
    width: 18px;
    height: 18px;
  }
  .reset-btn {
    padding: 7px 18px;
    font-size: 14px;
  }
  .item-label {
    font-size: 15px;
    min-width: 22px;
    margin-right: 7px;
  }
}

/* Mobile landscape & small tablet (768px - 481px) */
@media (max-width: 768px) {
  .columns {
    flex-direction: row;
    overflow-x: auto;
    width: 100%;
    min-width: 320px;
    gap: 15px;
  }
  .column {
    width: 48%;
    min-width: 120px;
    max-width: 100%;
    box-sizing: border-box;
  }
  .column.right {
    margin-right: 15px;
  }
  .matching-question-line {
    max-width: 100vw;
    padding: 0px 1vw 24px 1vw;
  }
  .line-item {
    padding: 6px;
    min-height: 36px;
    /* Removed pointer-events: none to allow drag from entire item area on mobile */
  }
  /* Disable hover effects on mobile only */
  .interactive-line:hover {
    filter: none !important;
  }
  .item-content >>> img {
    max-width: 100%;
    display: block;
    margin: 0 auto;
  }
  .item-content >>> p {
    max-width: 100%;
    display: block;
    margin: 0 auto;
  }
    
  .matching-instructions {
    font-size: 12px;
    padding: 5px 8px;
  }
  .dot {
    width: 14px;
    height: 14px;
  }
  .reset-btn {
    padding: 6px 15px;
    font-size: 13px;
  }
  .item-label {
    font-size: 14px;
    min-width: 20px;
    margin-right: 6px;
  }
}
</style>